import axios from "axios";

export type TokenGetter = () => Promise<string>;

/**
 * Creates an Axios client that fetches the access token per request via an interceptor.
 * This ensures tokens are always fresh and handles refresh/expiry naturally.
 */
export const createApiClient = (
    baseUrl: string,
    getAccessToken: TokenGetter,
    extraHeaders?: any,
    validateStatus?: number[]
) => {
    const client = axios.create({
        baseURL: baseUrl,
        headers: {
            'Content-Type': 'application/json',
            ...extraHeaders,
        },
        ...(validateStatus && {
            validateStatus: (status: number) => {
                return status >= 200 && status < 303 || validateStatus.includes(status);
            }
        }),
    });

    // Attach Authorization header per request
    client.interceptors.request.use(async (config) => {
        try {
            const token = await getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
                localStorage.setItem('accessToken', token);
            }
            return config;
        } catch (error) {
            // Token retrieval failed (e.g., refresh failed) - reject the request explicitly
            return Promise.reject(error);
        }
    });

    return client;
}

// localhost-only hack for authentication without an API-GW, adds the required header for the authentication to work
export const getExtraHeaders = (env: string) => {
    return {
        ...(env === 'localhost' && sessionStorage.getItem('commonUIUserId') && {
            'X-User-Id': sessionStorage.getItem('commonUIUserId'),
        }),
    };
};
